import { useTranslation } from 'react-i18next'
import {
  Button,
  CheckboxInput,
  H3,
  LoadingDots,
  RadioInput,
  Text,
} from '@/components/atoms'
import { SelectField } from '@/components/molecules'
import { LANGUAGE } from '@/shared/constants/language'
import { CONTACT_CHANNELS } from '@/shared/constants/onboarding'
import {
  useRentersStepTwoPage,
  type UserGender,
} from './page.onboarding-renters-step-two.hook'

interface RentersStepTwoPageProps {
  onContinue: () => void
  onBack: () => void
}

export function RentersStepTwoPage({
  onContinue,
  onBack,
}: RentersStepTwoPageProps) {
  const { t } = useTranslation()
  const {
    talkedLanguage,
    setTalkedLanguage,
    gender,
    setGender,
    genderOptions,
    channels,
    toggleChannel,
    handleContinue,
    handleBack,
    isLoading,
    isFormValid,
  } = useRentersStepTwoPage({ onContinue, onBack })

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:flex md:flex-row md:justify-between md:pb-0">
        <div className="flex flex-col justify-between gap-4 md:max-w-98">
          <div className="flex flex-col gap-4 md:max-w-90 md:gap-6">
            <H3 className="text-neutral-900">
              {t('onboarding.renters.stepTwo.title')}
            </H3>
            <Text className="text-sm text-neutral-900 md:text-base">
              {t('onboarding.renters.stepTwo.description')}
            </Text>
          </div>

          <div className="hidden gap-3 md:mb-2.5 md:flex">
            <Button
              variant="secondary"
              onClick={handleBack}
              data-testid="step-two-back"
              className="h-12"
            >
              {t('onboarding.back')}
            </Button>
            <Button
              onClick={handleContinue}
              data-testid="step-two-continue"
              className="h-12"
              disabled={!isFormValid}
            >
              {t('onboarding.continue')}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4 md:mt-0 md:max-w-72.5 md:gap-8">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <LoadingDots />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                <Text className="font-medium text-neutral-900">
                  {t('onboarding.renters.stepTwo.languagePreference')}
                </Text>
                <div className="flex gap-4">
                  <RadioInput
                    name="language"
                    label={t('onboarding.renters.stepTwo.english')}
                    value={LANGUAGE.EN}
                    checked={talkedLanguage === LANGUAGE.EN}
                    onChange={() => setTalkedLanguage(LANGUAGE.EN)}
                  />
                  <RadioInput
                    name="language"
                    label={t('onboarding.renters.stepTwo.spanish')}
                    value={LANGUAGE.ES}
                    checked={talkedLanguage === LANGUAGE.ES}
                    onChange={() => setTalkedLanguage(LANGUAGE.ES)}
                  />
                </div>
              </div>

              <SelectField
                label={t('onboarding.renters.stepTwo.identityLabel')}
                value={gender}
                options={genderOptions}
                placeholder={t(
                  'onboarding.renters.stepTwo.identityPlaceholder',
                )}
                onSelect={(v) => setGender(v as UserGender)}
                data-testid="step-two-gender-select"
              />

              <div className="flex flex-col gap-3">
                <Text className="font-medium text-neutral-900">
                  {t('onboarding.renters.stepTwo.preferredChannels')}
                </Text>
                <div className="flex flex-wrap gap-4">
                  {CONTACT_CHANNELS.map((channel) => (
                    <CheckboxInput
                      key={channel}
                      label={t(
                        `onboarding.renters.stepTwo.channels.${channel}`,
                      )}
                      checked={channels.has(channel)}
                      onChange={() => toggleChannel(channel)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex w-full gap-3 pt-4 md:hidden">
        <Button
          variant="secondary"
          onClick={handleBack}
          className="h-12 w-full"
          data-testid="step-two-back"
        >
          {t('onboarding.back')}
        </Button>
        <Button
          onClick={handleContinue}
          className="h-12 w-full"
          data-testid="step-two-continue"
          disabled={!isFormValid}
        >
          {t('onboarding.continue')}
        </Button>
      </div>
    </div>
  )
}
